/* eslint-disable max-lines */
import { z } from "zod";

// Cast member data structure
export const CastMember = z.object({
  name: z.string(),
  character: z.string().optional(),
  profileUrl: z.string().url().nullable().optional(),
});

export type ApiCastMember = z.infer<typeof CastMember>;

// Crew member data structure
export const CrewMember = z.object({
  name: z.string(),
  job: z.string(),
  department: z.string().optional(),
});

export type ApiCrewMember = z.infer<typeof CrewMember>;

export const Title = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  submissionId: z.string().nullable().optional(), // Link to source submission (for provider analytics)
  synopsis: z.string().nullable().optional(),
  description: z.string().nullable().optional(), // Extended description/synopsis
  year: z.number().nullable().optional(),
  durationSec: z.number().nullable().optional(),
  posterUrl: z.string().url().nullable().optional(),
  heroUrl: z.string().url().nullable().optional(),
  createdAt: z.string().nullable().optional(), // ISO date string for NEW badge
  // TMDB enriched fields
  tmdbId: z.number().nullable().optional(),
  country: z.string().nullable().optional(),
  runtimeMinutes: z.number().nullable().optional(),
  directorName: z.string().nullable().optional(),
  castNames: z.array(z.string()).nullable().optional(),
  castData: z.array(CastMember).nullable().optional(), // Enhanced cast data with images
  crewData: z.array(CrewMember).nullable().optional(), // Enhanced crew data with roles
  genres: z.array(z.string()).nullable().optional(),
  posterUrlHd: z.string().url().nullable().optional(),
  backdropUrlHd: z.string().url().nullable().optional(),
  voteAverage: z.number().nullable().optional(),
  productionCompanies: z.array(z.string()).nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
  logoSource: z.string().nullable().optional(),
  languages: z.array(z.string()).nullable().optional(),
  resolution: z.string().nullable().optional(),
  horizontalTileUrl: z.string().url().nullable().optional(),
  verticalTileUrl: z.string().url().nullable().optional(),
  // Provider slug for linking to provider profile page
  providerSlug: z.string().nullable().optional(),
});

export type ApiTitle = z.infer<typeof Title>;

export const Subtitle = z.object({
  lang: z.string(),
  url: z.string().url(),
  forced: z.boolean(),
});

export const Asset = z.object({
  id: z.string(),
  hlsUrl: z.string().url(),
  kind: z.enum(["FEATURE", "TRAILER", "TEASER"]).optional(), // Asset type
  subtitles: z.array(Subtitle),
  thumbnailVttUrl: z.string().url().nullable(),
  expiresAt: z.string().optional(), // ISO date string - when signed URL expires (Netflix-style security)
});

export type ApiAsset = z.infer<typeof Asset>;

export const Progress = z.object({
  assetId: z.string(),
  positionSec: z.number(),
  durationSec: z.number().optional(),
});

export const ContinueWatchingItem = z.object({
  title: Title,
  assetId: z.string(),
  positionSec: z.number(),
  durationSec: z.number(),
  progressPercent: z.number(),
  lastWatched: z.string(), // ISO date string
});

export type ApiContinueWatchingItem = z.infer<typeof ContinueWatchingItem>;

export const ContinueWatchingResponse = z.object({
  items: z.array(ContinueWatchingItem),
  hasMore: z.boolean(),
});

export const CollectionInfo = z.object({
  key: z.string(),
  name: z.string(),
  sort: z.number(),
  cardType: z.enum(["VERTICAL", "HORIZONTAL"]).default("VERTICAL"),
});

export type ApiCollectionInfo = z.infer<typeof CollectionInfo>;

export const CollectionItem = z.object({
  rank: z.number(),
  title: Title,
});

export const CollectionResponse = z.object({
  collection: CollectionInfo,
  items: z.array(CollectionItem),
});

export type ApiCollectionResponse = z.infer<typeof CollectionResponse>;

export const User = z.object({
  id: z.string(),
  email: z.string().email(),
  role: z.enum(["ADMIN", "CONTENT_PROVIDER", "VIEWER"]),
});

// Content Provider Profile
export const SocialLinks = z.object({
  twitter: z.string().url().optional(),
  instagram: z.string().url().optional(),
  youtube: z.string().url().optional(),
  vimeo: z.string().url().optional(),
  imdb: z.string().url().optional(),
  linkedin: z.string().url().optional(),
  website: z.string().url().optional(),
});

export type ApiSocialLinks = z.infer<typeof SocialLinks>;

export const ContentProvider = z.object({
  id: z.string(),
  userId: z.string(),
  slug: z.string(),
  displayName: z.string(),
  bio: z.string().nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
  logoSqUrl: z.string().url().nullable().optional(), // Square logo (1:1) for collection rails
  bannerUrl: z.string().url().nullable().optional(),
  websiteUrl: z.string().url().nullable().optional(),
  socialLinks: SocialLinks.nullable().optional(),
  verified: z.boolean(),
  featuredRank: z.number().nullable().optional(),
  createdAt: z.string(), // ISO date string
  updatedAt: z.string(), // ISO date string
});

export type ApiContentProvider = z.infer<typeof ContentProvider>;

export const ProviderProfile = ContentProvider.extend({
  titleCount: z.number(),
  totalViews: z.number().optional(),
});

export type ApiProviderProfile = z.infer<typeof ProviderProfile>;

export const UpdateProviderProfileRequest = z.object({
  displayName: z.string().min(1).max(255).optional(),
  bio: z.string().max(2000).optional(),
  logoUrl: z.string().url().optional().nullable(),
  logoSqUrl: z.string().url().optional().nullable(), // Square logo for collection rails
  bannerUrl: z.string().url().optional().nullable(),
  websiteUrl: z.string().url().optional().nullable(),
  socialLinks: SocialLinks.optional().nullable(),
});

export type ApiUpdateProviderProfileRequest = z.infer<typeof UpdateProviderProfileRequest>;

// Public Provider Profile Types (for /providers/:slug public page)
export const PublicProviderProfile = z.object({
  slug: z.string(),
  displayName: z.string(),
  bio: z.string().nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
  bannerUrl: z.string().url().nullable().optional(),
  websiteUrl: z.string().url().nullable().optional(),
  socialLinks: SocialLinks.nullable().optional(),
  verified: z.boolean(),
  titleCount: z.number(),
  titles: z.array(Title), // Full title data for CollectionRail compatibility
});

export type ApiPublicProviderProfile = z.infer<typeof PublicProviderProfile>;

export const FeaturedProvider = z.object({
  slug: z.string(),
  displayName: z.string(),
  logoUrl: z.string().url().nullable().optional(),
  logoSqUrl: z.string().url().nullable().optional(), // Square logo for collection rails
  verified: z.boolean(),
  titleCount: z.number(),
});

export type ApiFeaturedProvider = z.infer<typeof FeaturedProvider>;

export const FeaturedProvidersResponse = z.object({
  providers: z.array(FeaturedProvider),
});

export type ApiFeaturedProvidersResponse = z.infer<typeof FeaturedProvidersResponse>;

// Collection item containing a provider (for PROVIDER template collections)
export const CollectionProviderItem = z.object({
  rank: z.number(),
  provider: z.object({
    id: z.string(),
    slug: z.string(),
    displayName: z.string(),
    logoUrl: z.string().url().nullable().optional(),
    logoSqUrl: z.string().url().nullable().optional(),
    verified: z.boolean(),
  }),
});

export type ApiCollectionProviderItem = z.infer<typeof CollectionProviderItem>;

// Provider Analytics Types
export const ProviderAnalyticsOverview = z.object({
  totalViews: z.number(),
  uniqueViewers: z.number(),
  totalWatchTimeMinutes: z.number(),
  avgCompletionRate: z.number(),
});

export type ApiProviderAnalyticsOverview = z.infer<typeof ProviderAnalyticsOverview>;

export const ProviderTitleMetric = z.object({
  titleId: z.string(),
  name: z.string(),
  posterUrl: z.string().url().nullable().optional(),
  views: z.number(),
  uniqueViewers: z.number(),
  watchTimeMinutes: z.number(),
  completionRate: z.number(),
});

export type ApiProviderTitleMetric = z.infer<typeof ProviderTitleMetric>;

export const ProviderAnalyticsTrend = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  views: z.number(),
  watchTime: z.number(), // minutes
});

export type ApiProviderAnalyticsTrend = z.infer<typeof ProviderAnalyticsTrend>;

export const ProviderTitleTrend = z.object({
  titleId: z.string(),
  name: z.string(),
  data: z.array(ProviderAnalyticsTrend),
});

export type ApiProviderTitleTrend = z.infer<typeof ProviderTitleTrend>;

export const ProviderAnalyticsResponse = z.object({
  overview: ProviderAnalyticsOverview,
  titles: z.array(ProviderTitleMetric),
  trends: z.object({
    daily: z.array(ProviderAnalyticsTrend), // Aggregated totals
    byTitle: z.array(ProviderTitleTrend), // Per-title breakdown
  }),
});

export type ApiProviderAnalyticsResponse = z.infer<typeof ProviderAnalyticsResponse>;

export const TitleAnalyticsResponse = z.object({
  titleId: z.string(),
  name: z.string(),
  overview: ProviderAnalyticsOverview,
  viewsByDay: z.array(ProviderAnalyticsTrend),
  completionDistribution: z.array(
    z.object({
      bucket: z.string(), // e.g., "0-25%", "25-50%", "50-75%", "75-100%"
      count: z.number(),
    }),
  ),
});

export type ApiTitleAnalyticsResponse = z.infer<typeof TitleAnalyticsResponse>;

// Title Likes
export const TitleLike = z.object({
  userId: z.string(),
  titleId: z.string(),
  createdAt: z.string(), // ISO date string
});

export type ApiTitleLike = z.infer<typeof TitleLike>;

export const TitleLikeStatus = z.object({
  liked: z.boolean(),
  totalLikes: z.number(),
});

export type ApiTitleLikeStatus = z.infer<typeof TitleLikeStatus>;

export const LikedTitlesResponse = z.object({
  items: z.array(
    z.object({
      title: Title,
      likedAt: z.string(), // ISO date string
    }),
  ),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  hasMore: z.boolean(),
});

export type ApiLikedTitlesResponse = z.infer<typeof LikedTitlesResponse>;

// Watchlist
export const WatchlistItem = z.object({
  userId: z.string(),
  titleId: z.string(),
  createdAt: z.string(), // ISO date string
});

export type ApiWatchlistItem = z.infer<typeof WatchlistItem>;

export const WatchlistStatus = z.object({
  inWatchlist: z.boolean(),
});

export type ApiWatchlistStatus = z.infer<typeof WatchlistStatus>;

export const WatchlistResponse = z.object({
  items: z.array(
    z.object({
      title: Title,
      addedAt: z.string(), // ISO date string
    }),
  ),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  hasMore: z.boolean(),
});

export type ApiWatchlistResponse = z.infer<typeof WatchlistResponse>;

export const LoginRequest = z.object({
  email: z.string().email(),
  password: z.string(),
});

export const LoginResponse = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  user: User,
});

export const RefreshRequest = z.object({
  refreshToken: z.string(),
});

export const RefreshResponse = z.object({
  accessToken: z.string(),
});

export const RegisterRequest = z.object({
  email: z.string().email(),
  password: z.string(),
});

export const RegisterResponse = LoginResponse;

// Type for the refresh callback
export type RefreshTokenCallback = () => Promise<{ accessToken: string } | null>;

async function api<Out>(
  base: string,
  path: string,
  schema: z.ZodType<Out>,
  init?: RequestInit,
  accessToken?: string,
  refreshCallback?: RefreshTokenCallback,
): Promise<Out> {
  const makeRequest = async (token?: string): Promise<Response> => {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...init?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Add retry logic for network failures (e.g., CORS timing issues)
    let lastError: Error = new Error("Request failed after retries");
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        return await fetch(`${base}${path}`, {
          ...init,
          headers,
        });
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (attempt < 3) {
          // Wait a bit before retrying (exponential backoff)
          await new Promise((resolve) => setTimeout(resolve, attempt * 100));
          continue;
        }
        throw lastError;
      }
    }
    throw lastError;
  };

  let res = await makeRequest(accessToken);

  // If we got a 401 and have a refresh callback, try refreshing the token
  if (res.status === 401 && refreshCallback && accessToken) {
    console.log(`API ${path} returned 401, attempting token refresh...`);
    try {
      const refreshResult = await refreshCallback();
      if (refreshResult?.accessToken) {
        console.log(`Token refreshed, retrying ${path}...`);
        // Retry with new token
        res = await makeRequest(refreshResult.accessToken);
      }
    } catch (error) {
      console.error("Token refresh failed during API call:", error);
      // Fall through to original error handling
    }
  }

  if (!res.ok) {
    // Try to get error message from response body
    let errorMessage = res.statusText;
    try {
      const errorBody = (await res.json()) as { message?: string; error?: string };
      if (errorBody.message) {
        errorMessage = errorBody.message;
      } else if (errorBody.error) {
        errorMessage = errorBody.error;
      }
    } catch {
      // If JSON parsing fails, use statusText
    }
    throw new Error(`API ${path} ${res.status}: ${errorMessage}`);
  }

  const json: unknown = await res.json();
  return schema.parse(json);
}

// eslint-disable-next-line max-lines-per-function
export function createClient(apiBase: string, accessToken?: string, refreshCallback?: RefreshTokenCallback) {
  return {
    // Authentication methods
    async login(credentials: z.infer<typeof LoginRequest>) {
      return api(apiBase, "/v1/auth/login", LoginResponse, {
        method: "POST",
        body: JSON.stringify(credentials),
      });
    },

    async register(credentials: z.infer<typeof RegisterRequest>) {
      return api(apiBase, "/v1/auth/register", RegisterResponse, {
        method: "POST",
        body: JSON.stringify(credentials),
      });
    },

    async refreshToken(request: z.infer<typeof RefreshRequest>) {
      return api(apiBase, "/v1/auth/refresh", RefreshResponse, {
        method: "POST",
        body: JSON.stringify(request),
      });
    },

    // Content methods
    listTitles() {
      return api(apiBase, "/v1/titles", z.array(Title));
    },

    getAsset(id: string) {
      return api(apiBase, `/v1/assets/${id}`, Asset);
    },

    // Get all assets for a title (with authentication and auto-refresh)
    listTitleAssets(titleId: string) {
      return api(apiBase, `/v1/titles/${titleId}/assets`, z.array(Asset), undefined, accessToken, refreshCallback);
    },

    // Get title assets with fresh signed URLs (for token refresh)
    async getTitleAssets(titleId: string) {
      const assets = await api(
        apiBase,
        `/v1/titles/${titleId}/assets`,
        z.array(Asset),
        undefined,
        accessToken,
        refreshCallback,
      );
      // Return first FEATURE asset or fallback to first asset
      const featureAsset = assets.find((a) => a.kind === "FEATURE") || assets[0];
      if (!featureAsset) {
        throw new Error("No assets found for title");
      }
      return {
        hlsUrl: featureAsset.hlsUrl,
        subtitles: featureAsset.subtitles,
      };
    },

    // Progress methods (support authenticated requests)
    getProgress(assetId: string) {
      return api(
        apiBase,
        `/v1/progress/${assetId}`,
        z.object({ positionSec: z.number() }),
        undefined,
        accessToken,
        refreshCallback,
      );
    },

    postProgress(p: z.infer<typeof Progress>) {
      return api(
        apiBase,
        `/v1/progress`,
        z.object({ ok: z.boolean() }),
        {
          method: "POST",
          body: JSON.stringify(p),
        },
        accessToken,
        refreshCallback,
      );
    },

    // Continue watching content (requires authentication)
    getContinueWatching() {
      return api(
        apiBase,
        "/v1/user/continue-watching",
        ContinueWatchingResponse,
        undefined,
        accessToken,
        refreshCallback,
      );
    },

    // Collections methods
    getCollection(key: string) {
      return api(apiBase, `/v1/collections/${key}`, CollectionResponse);
    },

    // Title Likes methods (require authentication)
    async toggleTitleLike(titleId: string) {
      return api(
        apiBase,
        `/v1/titles/${titleId}/like`,
        z.object({ liked: z.boolean() }),
        {
          method: "POST",
        },
        accessToken,
        refreshCallback,
      );
    },

    async getTitleLikeStatus(titleId: string) {
      return api(
        apiBase,
        `/v1/titles/${titleId}/like-status`,
        TitleLikeStatus,
        undefined,
        accessToken,
        refreshCallback,
      );
    },

    async getLikedTitles(page = 1, pageSize = 20) {
      return api(
        apiBase,
        `/v1/users/me/likes?page=${page}&pageSize=${pageSize}`,
        LikedTitlesResponse,
        undefined,
        accessToken,
        refreshCallback,
      );
    },

    // Watchlist methods (require authentication)
    async addToWatchlist(titleId: string) {
      return api(
        apiBase,
        `/v1/watchlist/${titleId}`,
        z.object({ ok: z.boolean() }),
        {
          method: "POST",
        },
        accessToken,
        refreshCallback,
      );
    },

    async removeFromWatchlist(titleId: string) {
      return api(
        apiBase,
        `/v1/watchlist/${titleId}`,
        z.object({ ok: z.boolean() }),
        {
          method: "DELETE",
        },
        accessToken,
        refreshCallback,
      );
    },

    async getWatchlistStatus(titleId: string) {
      return api(apiBase, `/v1/watchlist/status/${titleId}`, WatchlistStatus, undefined, accessToken, refreshCallback);
    },

    async getWatchlist(page = 1, pageSize = 20) {
      return api(
        apiBase,
        `/v1/watchlist?page=${page}&pageSize=${pageSize}`,
        WatchlistResponse,
        undefined,
        accessToken,
        refreshCallback,
      );
    },

    // Helper to create an authenticated client
    withAuth(newAccessToken: string, newRefreshCallback?: RefreshTokenCallback) {
      return createClient(apiBase, newAccessToken, newRefreshCallback);
    },
  };
}

// Convenience default client for local dev
export const client = createClient("http://localhost:3333");

/**
 * Parse error message from response body
 */
async function parseErrorMessage(response: Response): Promise<string> {
  const defaultMessage = `Request failed: ${response.status} ${response.statusText}`;

  try {
    const errorBody = (await response.json()) as { error?: string; details?: string[] };
    if (errorBody.error) {
      let message = errorBody.error;
      if (errorBody.details && errorBody.details.length > 0) {
        message += `\n- ${errorBody.details.join("\n- ")}`;
      }
      return message;
    }
  } catch {
    // If parsing fails, use default message
  }

  return defaultMessage;
}

/**
 * Attempt to refresh token and retry request
 */
async function tryRefreshAndRetry(
  url: string,
  makeRequest: (token: string) => Promise<Response>,
  refreshCallback: RefreshTokenCallback,
): Promise<Response | null> {
  console.log(`Request to ${url} returned 401, attempting token refresh...`);

  try {
    const refreshResult = await refreshCallback();
    if (refreshResult?.accessToken) {
      console.log(`Token refreshed, retrying ${url}...`);
      return await makeRequest(refreshResult.accessToken);
    }
  } catch (error) {
    console.error("Token refresh failed during fetch:", error);
  }

  return null;
}

/**
 * Generic authenticated fetch helper with automatic token refresh on 401
 * Use this for Provider Portal and other authenticated API calls
 */
export async function authenticatedFetch<T = unknown>(
  url: string,
  options: RequestInit,
  accessToken: string,
  refreshCallback?: RefreshTokenCallback,
): Promise<T> {
  const makeRequest = async (token: string): Promise<Response> => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  };

  let response = await makeRequest(accessToken);

  // If we got a 401 and have a refresh callback, try refreshing the token
  if (response.status === 401 && refreshCallback) {
    const retryResponse = await tryRefreshAndRetry(url, makeRequest, refreshCallback);
    if (retryResponse) {
      response = retryResponse;
    }
  }

  if (!response.ok) {
    const errorMessage = await parseErrorMessage(response);
    throw new Error(errorMessage);
  }

  // Handle 204 No Content - don't try to parse JSON
  if (response.status === 204) {
    return undefined as T;
  }

  // For other successful responses, parse JSON
  return response.json() as Promise<T>;
}

// Export TMDB client and types
export * from "./tmdb";

// Export telemetry service and types
export * from "./telemetry";
